import React, { useState, useRef, useEffect } from 'react';
import { PlusIcon, TrashIcon, ArrowUpIcon, ArrowDownIcon, LockClosedIcon } from '@heroicons/react/24/outline';
import { useQuery } from '@tanstack/react-query';
import { ResponseType } from './QuestionInputs';
import api, { notificationAPI } from '../../services/api';
import { sanitizeTextOnInput, validateTextForSubmission } from '../../utils/textValidation';

const DEFAULT_CHOICES = ['Excellent', 'Good', 'Neutral', 'Poor', 'Very Poor'];

export interface SurveyQuestion {
  id: string;
  question: string;
  responseType: ResponseType;
  required: boolean;
  choices: string[];
  order: number;
}

export interface SurveyTemplateFormData {
  name: string;
  description: string;
  surveyType: 'incident_satisfaction' | 'change_satisfaction' | 'service_satisfaction' | 'custom';
  active: boolean;
  emailTemplateId?: string;
  questions: SurveyQuestion[];
}

interface SurveyTemplateFormProps {
  onSubmit: (data: SurveyTemplateFormData) => Promise<void>;
  onCancel?: () => void;
  initialData?: Partial<SurveyTemplateFormData>;
  loading?: boolean;
  readOnly?: boolean; // View/Preview mode - makes form read-only
}

const SurveyTemplateForm: React.FC<SurveyTemplateFormProps> = ({
  onSubmit,
  onCancel,
  initialData,
  loading = false,
  readOnly = false
}) => {
  // Fetch active default questions from API
  const { data: defaultQuestionsResponse, isLoading: isLoadingDefaultQuestions } = useQuery({
    queryKey: ['default-questions'],
    queryFn: async () => {
      const response = await api.get('/default-questions');
      return response.data;
    }
  });

  const defaultQuestionsFromDb: SurveyQuestion[] = (defaultQuestionsResponse?.data || []).filter((q: any) => q.active);
  
  // Determine if this is a new template (no initialData or no questions in initialData)
  const isNewTemplate = !initialData || !initialData.questions || initialData.questions.length === 0;

  // Fetch active survey email templates from API
  // Always refetch on mount to ensure latest templates are loaded
  const { data: emailTemplatesResponse, isLoading: isLoadingEmailTemplates } = useQuery({
    queryKey: ['survey-email-templates'],
    queryFn: async () => {
      const response = await notificationAPI.surveyEmailTemplates.list({ isActive: true });
      return response.data;
    },
    refetchOnMount: true,
    staleTime: 0 // Always consider data stale to ensure fresh data on every mount
  });

  // Fetch the currently selected template if it exists and is not in the active list (for edit mode)
  const { data: selectedTemplateResponse } = useQuery({
    queryKey: ['survey-email-template', initialData?.emailTemplateId],
    queryFn: async () => {
      if (!initialData?.emailTemplateId || isNewTemplate) return null;
      try {
        const response = await notificationAPI.surveyEmailTemplates.get(initialData.emailTemplateId);
        return response.data?.data || null;
      } catch (error) {
        // Template might not exist, return null
        return null;
      }
    },
    enabled: !!initialData?.emailTemplateId && !isNewTemplate,
  });

  // Get active email templates
  const activeEmailTemplates = emailTemplatesResponse?.data || [];
  const selectedTemplate = selectedTemplateResponse;
  
  // Build the final list: active templates + selected template (if inactive and in edit mode)
  const emailTemplates = React.useMemo(() => {
    const templates = [...activeEmailTemplates];
    
    // If editing and selected template is not in active list, add it
    if (selectedTemplate && !isNewTemplate) {
      const isInActiveList = activeEmailTemplates.some((t: any) => t.id === selectedTemplate.id);
      if (!isInActiveList) {
        templates.push(selectedTemplate);
      }
    }
    
    return templates;
  }, [activeEmailTemplates, selectedTemplate, isNewTemplate]);
  
  // Create array of default question texts for identification
  const defaultQuestionTexts = defaultQuestionsFromDb.map(q => q.question);
  
  // Convert active default questions from DB to SurveyQuestion format
  const defaultQuestionsForForm: SurveyQuestion[] = defaultQuestionsFromDb.map((q, index) => ({
    id: `default_${q.id}`,
    question: q.question,
    responseType: q.responseType as ResponseType,
    required: q.required,
    choices: q.choices || [],
    order: index
  }));
  
  // For new templates, include active default questions so users can see them in the form
  // For existing templates, use their questions (including default questions from backend)
  const initialQuestions = isNewTemplate 
    ? defaultQuestionsForForm
    : (initialData?.questions || []);

  const [formData, setFormData] = useState<SurveyTemplateFormData>({
    name: initialData?.name ?? '',
    description: initialData?.description ?? '',
    surveyType: 'custom', // Always 'custom', hidden from UI
    active: initialData?.active !== undefined ? initialData.active : false,
    emailTemplateId: initialData?.emailTemplateId ?? '',
    questions: initialQuestions
  });

  // Update formData when initialData changes (for edit mode)
  useEffect(() => {
    if (initialData) {
      console.log('Updating formData from initialData:', {
        emailTemplateId: initialData.emailTemplateId,
        name: initialData.name
      });
      setFormData(prev => ({
        ...prev,
        name: initialData.name ?? prev.name,
        description: initialData.description ?? prev.description,
        active: initialData.active !== undefined ? initialData.active : prev.active,
        emailTemplateId: initialData.emailTemplateId ?? prev.emailTemplateId ?? '',
        questions: initialData.questions ?? prev.questions
      }));
    }
  }, [initialData]);

  // Update formData.questions when active default questions are loaded for new templates
  useEffect(() => {
    if (isNewTemplate && !isLoadingDefaultQuestions && defaultQuestionsForForm.length > 0) {
      setFormData(prev => ({
        ...prev,
        questions: defaultQuestionsForForm
      }));
    }
  }, [isNewTemplate, isLoadingDefaultQuestions, defaultQuestionsForForm.length]);

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitError, setSubmitError] = useState<string>('');
  const [newQuestionId, setNewQuestionId] = useState<string | null>(null);
  const questionRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
  const questionInputRefs = useRef<{ [key: string]: HTMLInputElement | null }>({});

  const generateQuestionId = () => {
    return `q_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  };

  // Scroll to and focus on newly added question
  useEffect(() => {
    if (newQuestionId) {
      // Use setTimeout to ensure DOM is updated
      setTimeout(() => {
        const questionElement = questionRefs.current[newQuestionId];
        const inputElement = questionInputRefs.current[newQuestionId];

        if (questionElement) {
          // Smooth scroll to the question container first
          questionElement.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'center' 
          });

          // Wait for smooth scroll animation to complete before focusing
          // Smooth scroll typically takes 500-1000ms depending on scroll distance
          // Using a longer delay to ensure scroll completes before focus
          setTimeout(() => {
            if (inputElement) {
              inputElement.focus();
            }
          }, 800);
        }

        // Reset the new question ID
        setNewQuestionId(null);
      }, 100);
    }
  }, [newQuestionId, formData.questions.length]);

  const addQuestion = () => {
    const questionId = generateQuestionId();
    const newQuestion: SurveyQuestion = {
      id: questionId,
      question: '',
      responseType: 'text',
      required: false,
      choices: [],
      order: formData.questions.length
    };
    setFormData({
      ...formData,
      questions: [...formData.questions, newQuestion]
    });
    // Set the new question ID to trigger scroll and focus
    setNewQuestionId(questionId);
  };

  // Check if a question is a default question (by matching question text)
  const isDefaultQuestion = (question: SurveyQuestion) => {
    return defaultQuestionTexts.includes(question.question);
  };

  const removeQuestion = (questionId: string) => {
    const question = formData.questions.find(q => q.id === questionId);
    // Prevent deletion of default questions
    if (question && isDefaultQuestion(question)) {
      return;
    }
    
    setFormData({
      ...formData,
      questions: formData.questions
        .filter(q => q.id !== questionId)
        .map((q, index) => ({ ...q, order: index }))
    });
    // Clear related errors
    const newErrors = { ...errors };
    delete newErrors[`question_${questionId}`];
    delete newErrors[`choices_${questionId}`];
    setErrors(newErrors);
  };

  const updateQuestion = (questionId: string, updates: Partial<SurveyQuestion>) => {
    const question = formData.questions.find(q => q.id === questionId);
    // For default questions, prevent all modifications (including required field)
    if (question && isDefaultQuestion(question)) {
      // Block all updates to default questions - they are locked
      return;
    }
    
    setFormData({
      ...formData,
      questions: formData.questions.map(q =>
        q.id === questionId ? { ...q, ...updates } : q
      )
    });
    // Clear errors when updating
    if (updates.question !== undefined) {
      const newErrors = { ...errors };
      delete newErrors[`question_${questionId}`];
      setErrors(newErrors);
    }
  };

  const moveQuestion = (questionId: string, direction: 'up' | 'down') => {
    const question = formData.questions.find(q => q.id === questionId);
    // Prevent moving default questions
    if (question && isDefaultQuestion(question)) {
      return;
    }

    const index = formData.questions.findIndex(q => q.id === questionId);
    if (index === -1) return;

    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= formData.questions.length) return;

    // Prevent swapping with default questions
    const targetQuestion = formData.questions[newIndex];
    if (targetQuestion && isDefaultQuestion(targetQuestion)) {
      return;
    }

    const newQuestions = [...formData.questions];
    [newQuestions[index], newQuestions[newIndex]] = [newQuestions[newIndex], newQuestions[index]];
    
    // Update order values
    newQuestions.forEach((q, i) => {
      q.order = i;
    });

    setFormData({
      ...formData,
      questions: newQuestions
    });
  };

  const addChoice = (questionId: string) => {
    updateQuestion(questionId, {
      choices: [...(formData.questions.find(q => q.id === questionId)?.choices || []), '']
    });
  };

  const updateChoice = (questionId: string, choiceIndex: number, value: string) => {
    const question = formData.questions.find(q => q.id === questionId);
    if (!question) return;

    const newChoices = [...question.choices];
    newChoices[choiceIndex] = value;
    updateQuestion(questionId, { choices: newChoices });
    
    // Clear error when updating
    const newErrors = { ...errors };
    delete newErrors[`choices_${questionId}`];
    setErrors(newErrors);
  };

  const removeChoice = (questionId: string, choiceIndex: number) => {
    const question = formData.questions.find(q => q.id === questionId);
    if (!question) return;

    const newChoices = question.choices.filter((_, index) => index !== choiceIndex);
    updateQuestion(questionId, { choices: newChoices });
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    setSubmitError('');

    // Validate template name
    const nameValidation = validateTextForSubmission(formData.name, 'Template name', true);
    if (!nameValidation.isValid) {
      newErrors.name = nameValidation.error || 'Template name is required';
    }

    // Validate description (optional field)
    if (formData.description && formData.description.trim()) {
      const descriptionValidation = validateTextForSubmission(formData.description, 'Description', false);
      if (!descriptionValidation.isValid) {
        newErrors.description = descriptionValidation.error || '';
      }
    }

    if (!formData.emailTemplateId || !formData.emailTemplateId.trim()) {
      newErrors.emailTemplateId = 'Email template is required';
    }

    if (formData.questions.length === 0) {
      newErrors.questions = 'At least one question is required';
    }

    formData.questions.forEach((question, index) => {
      if (!question.question.trim()) {
        newErrors[`question_${question.id}`] = `Question ${index + 1} text is required`;
      }

      if (question.responseType === 'choice') {
        const validChoices = question.choices.filter(c => c.trim().length > 0);
        if (validChoices.length === 0) {
          newErrors[`choices_${question.id}`] = `Question ${index + 1} requires at least one choice`;
        }
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Prevent submission in read-only mode
    if (readOnly) {
      return;
    }
    
    setSubmitError('');

    if (!validate()) {
      return;
    }

    try {
      // Filter out default questions before submission - backend will create them automatically
      const questionsToSubmit = formData.questions.filter(q => 
        !isDefaultQuestion(q)
      );

      await onSubmit({
        ...formData,
        questions: questionsToSubmit
      });
    } catch (error: any) {
      console.error('Error submitting form:', error);
      setSubmitError(error.response?.data?.message || 'Failed to save survey template');
    }
  };

  return (
    <form onSubmit={handleSubmit} className="p-8">
      {submitError && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-md p-4">
          <p className="text-sm text-red-800">{submitError}</p>
        </div>
      )}

      {/* Row 1: Template Name */}
      <div className="grid grid-cols-12 gap-6 mb-6">
        <div className="col-span-12">
          <div className="grid grid-cols-12 gap-4 items-start">
            <div className="col-span-2">
              <label htmlFor="name" className="block text-left text-sm font-medium text-gray-700 mb-1 pt-2">
                Template Name <span className="text-red-500">*</span>
              </label>
            </div>
            <div className="col-span-10">
              <input
                type="text"
                id="name"
                value={formData.name}
                onChange={(e) => {
                  // Sanitize input in real-time to prevent HTML tags and dangerous characters
                  const sanitized = sanitizeTextOnInput(e.target.value);
                  setFormData({ ...formData, name: sanitized });
                  // Clear error when user starts typing
                  if (errors.name) {
                    setErrors({ ...errors, name: '' });
                  }
                }}
                disabled={readOnly}
                className={`w-full px-3 py-1 border rounded text-sm h-8 focus:outline-none focus:ring-1 focus:ring-blue-400 ${
                  errors.name 
                    ? 'border-red-300 focus:ring-red-400' 
                    : 'border-gray-300'
                } ${readOnly ? 'bg-gray-50 cursor-not-allowed' : ''}`}
                placeholder="e.g., Customer Satisfaction Survey"
              />
              {errors.name && (
                <p className="mt-1 text-sm text-red-600">{errors.name}</p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Row 2: Description */}
      <div className="grid grid-cols-12 gap-6 mb-6">
        <div className="col-span-12">
          <div className="grid grid-cols-12 gap-4 items-start">
            <div className="col-span-2">
              <label htmlFor="description" className="block text-left text-sm font-medium text-gray-700 mb-1 pt-2">
                Description
              </label>
            </div>
            <div className="col-span-10">
              <textarea
                id="description"
                value={formData.description}
                onChange={(e) => {
                  // Sanitize input in real-time to prevent HTML tags and dangerous characters
                  const sanitized = sanitizeTextOnInput(e.target.value);
                  setFormData({ ...formData, description: sanitized });
                  // Clear error when user starts typing
                  if (errors.description) {
                    setErrors({ ...errors, description: '' });
                  }
                }}
                disabled={readOnly}
                rows={3}
                className={`w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-400 ${
                  errors.description 
                    ? 'border-red-300 focus:ring-red-400' 
                    : ''
                } ${readOnly ? 'bg-gray-50 cursor-not-allowed' : ''}`}
                placeholder="Describe the purpose of this survey template..."
              />
              {errors.description && (
                <p className="mt-1 text-sm text-red-600">{errors.description}</p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Row 3: Email Template */}
      <div className="grid grid-cols-12 gap-6 mb-6">
        <div className="col-span-12">
          <div className="grid grid-cols-12 gap-4 items-start">
            <div className="col-span-2">
              <label htmlFor="emailTemplateId" className="block text-left text-sm font-medium text-gray-700 mb-1 pt-2">
                Email Template <span className="text-red-500">*</span>
              </label>
            </div>
            <div className="col-span-10">
              <select
                id="emailTemplateId"
                value={formData.emailTemplateId ?? ''}
                onChange={(e) => setFormData({ ...formData, emailTemplateId: e.target.value || undefined })}
                disabled={readOnly || isLoadingEmailTemplates}
                className={`w-full px-3 py-1 border rounded text-sm h-8 focus:outline-none focus:ring-1 focus:ring-blue-400 ${
                  errors.emailTemplateId 
                    ? 'border-red-300 focus:ring-red-400' 
                    : 'border-gray-300'
                } ${readOnly ? 'bg-gray-50 cursor-not-allowed' : ''}`}
                required
              >
                <option value="">-- Select an Email Template (Required) --</option>
                {isLoadingEmailTemplates ? (
                  <option disabled>Loading templates...</option>
                ) : (
                  emailTemplates.map((template: any) => {
                    const isSelectedInactive = !template.isActive && template.id === initialData?.emailTemplateId;
                    return (
                      <option 
                        key={template.id} 
                        value={template.id}
                        disabled={!template.isActive && !isSelectedInactive}
                      >
                        {template.name}{!template.isActive ? ' (Inactive)' : ''}
                      </option>
                    );
                  })
                )}
              </select>
              {errors.emailTemplateId && (
                <p className="mt-1 text-sm text-red-600">{errors.emailTemplateId}</p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Row 4: Active */}
      <div className="grid grid-cols-12 gap-6 mb-6">
        <div className="col-span-6">
          <div className="flex items-start">
            <div className="flex items-center h-5">
              <input
                type="checkbox"
                id="active"
                checked={formData.active}
                onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                disabled={readOnly}
                className="focus:ring-blue-500 h-4 w-4 text-blue-600 border-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed"
              />
            </div>
            <div className="ml-3 text-sm text-left">
              <label htmlFor="active" className="font-medium text-gray-700">
                Active
              </label>
              <p className="text-gray-500">Template is available for use</p>
            </div>
          </div>
        </div>
      </div>

      {/* Questions Section */}
      <div className="grid grid-cols-12 gap-6 mb-6">
        <div className="col-span-12">
          <div className="flex items-center justify-between mb-4">
            <div className="grid grid-cols-12 gap-4 items-center w-full">
              <div className="col-span-2">
                <label className="block text-left text-sm font-medium text-gray-700">
                  Questions
                </label>
              </div>
              {!readOnly && (
                <div className="col-span-10 flex justify-end">
                  <button
                    type="button"
                    onClick={addQuestion}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
                  >
                    <PlusIcon className="h-4 w-4 mr-1.5" />
                    Add Question
                  </button>
                </div>
              )}
            </div>
          </div>

          {errors.questions && (
            <div className="mb-4 bg-red-50 border border-red-200 rounded-md p-3">
              <p className="text-sm text-red-800">{errors.questions}</p>
            </div>
          )}

          <div className="space-y-4">
            {formData.questions.map((question, index) => {
              const isDefault = isDefaultQuestion(question);
              return (
              <div 
                key={question.id} 
                ref={(el) => { questionRefs.current[question.id] = el; }}
                className={`border rounded-lg p-4 ${
                  isDefault 
                    ? 'border-blue-300 bg-blue-50' 
                    : 'border-gray-200 bg-gray-50'
                }`}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center space-x-2">
                    <span className="text-sm font-medium text-gray-700">Question {index + 1}</span>
                    {isDefault && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                        <LockClosedIcon className="h-3 w-3 mr-1" />
                        System Default
                      </span>
                    )}
                    {!readOnly && !isDefault && (
                      <div className="flex items-center space-x-1">
                        <button
                          type="button"
                          onClick={() => moveQuestion(question.id, 'up')}
                          disabled={index === 0}
                          className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed"
                          title="Move up"
                        >
                          <ArrowUpIcon className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => moveQuestion(question.id, 'down')}
                          disabled={index === formData.questions.length - 1}
                          className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed"
                          title="Move down"
                        >
                          <ArrowDownIcon className="h-4 w-4" />
                        </button>
                      </div>
                    )}
                  </div>
                  {!readOnly && !isDefault && (
                    <button
                      type="button"
                      onClick={() => removeQuestion(question.id)}
                      className="text-red-400 hover:text-red-600"
                      title="Remove question"
                    >
                      <TrashIcon className="h-5 w-5" />
                    </button>
                  )}
                </div>

                <div className="space-y-4">
                  {/* Question Text */}
                  <div className="grid grid-cols-12 gap-4 items-start">
                    <div className="col-span-2">
                      <label className="block text-left text-sm font-medium text-gray-700 mb-1 pt-2">
                        Question Text <span className="text-red-500">*</span>
                      </label>
                    </div>
                    <div className="col-span-10">
                      <input
                        ref={(el) => { questionInputRefs.current[question.id] = el; }}
                        type="text"
                        value={question.question}
                        onChange={(e) => updateQuestion(question.id, { question: e.target.value })}
                        disabled={readOnly || isDefault}
                        className={`w-full px-3 py-1 border rounded text-sm h-8 focus:outline-none focus:ring-1 focus:ring-blue-400 ${
                          errors[`question_${question.id}`] 
                            ? 'border-red-300 focus:ring-red-400' 
                            : 'border-gray-300'
                        } ${readOnly || isDefault ? 'bg-gray-50 cursor-not-allowed' : ''}`}
                        placeholder="Enter your question..."
                      />
                      {errors[`question_${question.id}`] && (
                        <p className="mt-1 text-sm text-red-600">{errors[`question_${question.id}`]}</p>
                      )}
                    </div>
                  </div>

                  {/* Response Type and Required */}
                  <div className="grid grid-cols-12 gap-6">
                    <div className="col-span-6">
                      <div className="grid grid-cols-12 gap-4 items-end">
                        <div className="col-span-4">
                          <label className="block text-left text-sm font-medium text-gray-700 mb-1">
                            Response Type
                          </label>
                        </div>
                        <div className="col-span-8">
                          <select
                            value={question.responseType}
                            onChange={(e) => {
                              const newType = e.target.value as ResponseType;
                              if (newType === 'choice') {
                                // Auto-populate default choices when changing to 'choice'
                                updateQuestion(question.id, {
                                  responseType: newType,
                                  choices: [...DEFAULT_CHOICES]
                                });
                              } else {
                                // Clear choices for non-choice types
                                updateQuestion(question.id, {
                                  responseType: newType,
                                  choices: []
                                });
                              }
                            }}
                            disabled={readOnly || isDefault}
                            className={`w-full px-3 py-1 border border-gray-300 rounded text-sm h-8 focus:outline-none focus:ring-1 focus:ring-blue-400 ${readOnly || isDefault ? 'bg-gray-50 cursor-not-allowed' : ''}`}
                          >
                            <option value="text">Text</option>
                            <option value="choice">Single Choice</option>
                          </select>
                        </div>
                      </div>
                    </div>
                    <div className="col-span-6">
                      <div className="flex items-start">
                        <div className="flex items-center h-5">
                          <input
                            type="checkbox"
                            checked={question.required}
                            onChange={(e) => updateQuestion(question.id, { required: e.target.checked })}
                            disabled={readOnly}
                            className="focus:ring-blue-500 h-4 w-4 text-blue-600 border-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                          />
                        </div>
                        <div className="ml-3 text-sm text-left">
                          <label className="font-medium text-gray-700">
                            Required
                          </label>
                          <p className="text-gray-500">Question must be answered</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Choices for choice and multiple_choice */}
                  {question.responseType === 'choice' && (
                    <div className="grid grid-cols-12 gap-4 items-start">
                      <div className="col-span-2">
                        <div className="flex items-center justify-between mb-1 pt-2">
                          <label className="block text-left text-sm font-medium text-gray-700">
                            Choices <span className="text-red-500">*</span>
                          </label>
                        </div>
                      </div>
                      <div className="col-span-10">
                        {question.choices && question.choices.length > 0 ? (
                          <div className="flex flex-wrap gap-2">
                            {question.choices.map((choice, idx) => (
                              <span
                                key={idx}
                                className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-indigo-100 text-indigo-800"
                              >
                                {choice}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <p className="text-sm text-gray-500">No choices available</p>
                        )}
                        <p className="mt-2 text-xs text-gray-500 text-left">Default choices are automatically applied and cannot be modified</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              );
            })}
          </div>

          {/* Bottom Add Question Button - Only show when questions exist and not read-only */}
          {formData.questions.length > 0 && !readOnly && (
            <div className="flex justify-end pt-4">
              <button
                type="button"
                onClick={addQuestion}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
              >
                <PlusIcon className="h-4 w-4 mr-1.5" />
                Add Question
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Submit Button - Hidden in read-only mode */}
      {!readOnly && (
        <div className="sticky bottom-0 bg-white border-t border-gray-200 px-8 py-4 -mx-8 -mb-8 mt-6 flex justify-end gap-3 z-10">
          {onCancel && (
            <button
              type="button"
              onClick={onCancel}
              disabled={loading}
              className="px-6 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
          )}
          <button
            type="submit"
            disabled={loading}
            className="px-6 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {loading ? 'Saving...' : 'Save Template'}
          </button>
        </div>
      )}
    </form>
  );
};

export default SurveyTemplateForm;
